import React, { useState, useContext, useEffect } from 'react';

import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import TablePagination from '@mui/material/TablePagination';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import ViewRoleDialog from './ViewRoleDialog';
import AddRoleDialog from './AddRoleDialog';
import ConfirmDeleteDialog from './ConfirmDeleteRoleDialog';
import EditRoleDialog from './EditRoleDialog';
import { getIcon } from '../../../utils/IconHandler';
import { formatDate } from '../../../utils/DateUtils';
import { getRolesAndPermissions, addRole, deleteRole, editRole, instantiateRoleTemplate } from '../../../services/RoleService';
import AuthContext from '../../../context/AuthContext';
import { useTranslation } from 'react-i18next';

export default function RoleTable() {

  const { accessToken } = useContext(AuthContext);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [roleToEdit, setRoleToEdit] = useState(null);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [templateToInstantiate, setTemplateToInstantiate] = useState(null);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [allRoles, setRoles] = useState([]);
  const [allPermissions, setPermissions] = useState([]);
  const [allRolesPermissions, setRolesPermissions] = useState({});
  const [roleTemplates, setRoleTemplates] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { t } = useTranslation();
  const isSuperAdmin = localStorage.getItem('isSuperAdmin') === 'true';

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await getRolesAndPermissions();
        setRoles(data.roles);
        setPermissions(data.permissions);
        setRolesPermissions(data.roles_permissions);
        setRoleTemplates(data.role_templates || []);
      } catch (error) {
        console.error('Error fetching roles and permissions', error);
      }
    };

    fetchData();
  }, [accessToken]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleOpenViewDialog = (role) => {
    setSelectedRole(role);
    setOpenDialog(true);
  };

  const handleCloseViewDialog = () => {
    setOpenDialog(false);
    setSelectedRole(null);
  };

  const handleOpenAddDialog = () => {
    setTemplateToInstantiate(null);
    setOpenAddDialog(true);
  };

  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
    setTemplateToInstantiate(null);
  };

  const handleOpenConfirmDialog = (role) => {
    setRoleToDelete(role);
    setOpenConfirmDialog(true);
  };

  const handleCloseConfirmDialog = () => {
    setOpenConfirmDialog(false);
    setRoleToDelete(null);
  };

  const handleOpenEditDialog = (role) => {
    setRoleToEdit(role);
    setOpenEditDialog(true);
  };
  
  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setRoleToEdit(null);
  };

  const handleAddRole = async (roleData) => {
    try {
      const data = roleData.templateCode
        ? await instantiateRoleTemplate(roleData.templateCode, {
            name: roleData.name,
            description: roleData.description
          })
        : await addRole(roleData);
      setRoles(data.roles);
      setPermissions(data.permissions);
      setRolesPermissions(data.roles_permissions);
      setRoleTemplates(data.role_templates || []);
      setOpenAddDialog(false);
      setTemplateToInstantiate(null);
    } catch (error) {
      console.error('Error adding new role:', error);
    }
  };

  const handleDeleteRole = async (id) => {
    handleCloseConfirmDialog();
    try {
      const data = await deleteRole(id);
      setRoles(data.roles);
      setPermissions(data.permissions);
      setRolesPermissions(data.roles_permissions);
      setRoleTemplates(data.role_templates || []);
      handleCloseConfirmDialog();
    } catch (error) {
      console.error('Error deleting role:', error);
    }
  };
  
  const handleEditRole = async (roleData) => {
    try {
      const data = await editRole(roleToEdit.id, roleData);
      setRoles(data.roles);
      setPermissions(data.permissions);
      setRolesPermissions(data.roles_permissions);
      setRoleTemplates(data.role_templates || []);
      setOpenEditDialog(false);
    } catch (error) {
      console.error('Error editing role:', error);
    }
  };

  const handleInstantiateTemplate = async (template) => {
    setTemplateToInstantiate(template);
    setOpenAddDialog(true);
  };

  const instantiatedTemplateCodes = new Set(allRoles.map((role) => role.templateCode).filter(Boolean));

  const getScopeLabel = (scopeType) => {
    switch (scopeType) {
      case 'ALL':
        return 'Scope: Ecosistema';
      case 'MAIN':
        return 'Scope: Main';
      case 'BUSINESS_UNIT':
        return 'Scope: Franquicia';
      case 'LOCATION':
        return 'Scope: Sucursal';
      case 'ASSIGNED_LOCATIONS':
        return 'Scope: Sucursales asignadas';
      default:
        return 'Scope variable';
    }
  };

  return (
    <>
      <div className='header-container'>
        {isSuperAdmin && (
          <Button className="right_button_table" variant="outlined" startIcon={getIcon('add')} onClick={handleOpenAddDialog}>
            {t('add_rol')}
          </Button>
        )}
      </div>
      {isSuperAdmin && roleTemplates.length > 0 && (
        <Paper sx={{ width: '100%', overflow: 'hidden', marginBottom: 2, padding: 0 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                md: 'repeat(2, minmax(0, 1fr))',
                lg: 'repeat(3, minmax(0, 1fr))',
                xl: 'repeat(4, minmax(0, 1fr))'
              },
              gap: 2,
              width: '100%'
            }}
          >
            {roleTemplates.map((template) => {
              const isInstantiated = instantiatedTemplateCodes.has(template.code);
              return (
                <Card key={template.code} variant="outlined" sx={{ width: '100%', minWidth: 0 }}>
                  <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                    <span className="roboto-regular" style={{ display: 'block', marginBottom: 8 }}>{template.name}</span>
                    <span className="roboto-light" style={{ display: 'block', marginBottom: 8 }}>{template.description}</span>
                    <Chip
                      label={getScopeLabel(template.recommendedScopeType)}
                      size="small"
                      sx={{ alignSelf: 'flex-start' }}
                    />
                    <span className="roboto-light" style={{ display: 'block' }}>
                      {template.permissions?.length || 0} permisos
                    </span>
                    <Button
                      variant={isInstantiated ? 'outlined' : 'contained'}
                      size="small"
                      fullWidth
                      disabled={isInstantiated}
                      onClick={() => handleInstantiateTemplate(template)}
                    >
                      {isInstantiated ? 'Ya creado' : 'Crear desde template'}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        </Paper>
      )}
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer component={Paper}>
          <Table stickyHeader aria-label="sticky table">
            <TableHead>
              <TableRow>
                <TableCell width="15%"><span className="roboto-regular">{t('role')}</span></TableCell>
                <TableCell width="30%" align="center"><span className="roboto-regular" style={{ minWidth: 200 }}>{t('description')}</span></TableCell>
                <TableCell width="20%" align="center"><span className="roboto-regular">{t('users_using_the_role')}</span></TableCell>
                <TableCell width="20%" align="center"><span className="roboto-regular">{t('created_at')}</span></TableCell>
                <TableCell width="15%" align="right"><span className="roboto-regular" style={{ minWidth: 120 }}>{t('actions')}</span></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {allRoles
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((role) => {
                  return (
                    <TableRow key={role.id}>
                      <TableCell component="th" scope="row">
                        <span className="roboto-light">{role.name}</span>
                      </TableCell>
                      <TableCell align="center"><span className="roboto-light" style={{ minWidth: 200 }}>{role.description}</span></TableCell>
                      <TableCell align="center"><span className="roboto-light">{role.userCount}</span></TableCell>
                      <TableCell align="center"><span className="roboto-light">{formatDate(role.createdAt)}</span></TableCell>
                      <TableCell align="right" style={{ minWidth: 120 }}>
                        <IconButton onClick={() => handleOpenViewDialog(role)}>
                          {getIcon('eye')}
                        </IconButton>
                        <IconButton onClick={() => handleOpenEditDialog(role)}>
                          {getIcon('hammer')}
                        </IconButton>
                        {role.userCount === 0 ? (
                          <IconButton onClick={() => handleOpenConfirmDialog(role)} disabled={false}>
                            {getIcon('trash')}
                          </IconButton>
                        ) : (
                          <Tooltip title={t('cannot_delete_role_in_use')}>
                            <span>
                              <IconButton onClick={() => handleOpenConfirmDialog(role)} disabled={true}>
                                {getIcon('trash')}
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={allRoles.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      <AddRoleDialog 
        open={openAddDialog} 
        onClose={handleCloseAddDialog} 
        permissions={allPermissions} 
        onAddRole={handleAddRole}
        initialName={templateToInstantiate?.name || ''}
        initialDescription={templateToInstantiate?.description || ''}
        initialPermissions={templateToInstantiate?.permissions || []}
        permissionsEditable={!templateToInstantiate}
        title={templateToInstantiate ? 'Crear rol desde template' : undefined}
        saveLabel={templateToInstantiate ? 'Crear desde template' : undefined}
        templateCode={templateToInstantiate?.code || null}
      />

      {selectedRole && (
        <ViewRoleDialog
          open={openDialog}
          onClose={handleCloseViewDialog}
          role={selectedRole}
          permissions={allPermissions}
          rolesPermissions={allRolesPermissions}
        />
      )}
      
      {roleToEdit && (
        <EditRoleDialog
          name={roleToEdit.name}
          description={roleToEdit.description}
          permissions={allPermissions}
          rolePermissions={allRolesPermissions[roleToEdit.id] || []}
          open={openEditDialog}
          onClose={handleCloseEditDialog}
          onEditRole={handleEditRole}
        />
      )}
      
      {roleToDelete && (
        <ConfirmDeleteDialog
          open={openConfirmDialog}
          onClose={handleCloseConfirmDialog}
          onConfirm={() => handleDeleteRole(roleToDelete.id)}
          title={<span className='roboto-regular'>{t('confirm_deletion')}</span>}
          description={
            <div>
              <span className='roboto-light'>{t('confirm_deletion_description')} <span className='roboto-regular'>{roleToDelete.name}</span>?</span><br/>
              <span className='roboto-light'>{t('confirm_deletion_description_2')}.</span>
            </div>
          }
        />
      )}
    </>
  );
}
